/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { castToNonNullablePredicate } from "libs/predicates";
import { DateTime } from "luxon";
import * as parsing from "./parsing";
import commandScore from "command-score";
import { uniqBy } from "lodash-comms";
import { DateTimeSuggestion } from "./DateTimeSuggestion";
import { UnreachableCaseError } from "../errors";

/** Note that this can return suggestions with invalid dates */
export function matchDateTimeSuggestions(props: {
  input: string;
  suggestions: Array<DateTimeSuggestion | null>;
  limit: number;
  allowDatesInThePast?: boolean;
  now?: DateTime;
}) {
  const { input, suggestions, limit, now = DateTime.now(), allowDatesInThePast = false } = props;

  if (input.length === 0) {
    suggestions.forEach((s) => s?.finalizeUpdates(now));

    return suggestions.filter(castToNonNullablePredicate((s) => !!s?.content && !s.disabled)).slice(0, limit);
  }

  // Overview
  //
  // 1. We build up a tokenized representation of the
  // user input. For example, `9am on Monday` will be
  // tokenized as `{{TIME}} on Monday` while
  // `monday 9 9p` will be tokenized as
  // `monday {{NUMBER}} {{TIME}}`.
  //
  // 2. Later, having tokenized the user input and interpreted
  // the tokens, we'll reinsert the interpreted values
  // back into the user input to build a normalized user
  // input. For example
  // `9 monday` was tokenized as `{{NUMBER}} monday`
  // and then converted back to a normalized user
  // input as `9 am monday`. Similarly,
  // `jan 5 5` would go -> `jan {{NUMBER}} {{NUMBER}}`
  // and then `jan 5th 5 pm`.

  // NOTES:
  // 1. The order that we tokenize the user input matters.
  //    I.e. the order that these matchers are in matters.
  // 2. The regular expressions being passed to `matchAll()`
  //    have the "global" flag (which is required by
  //    `matchAll()`). The "global" flag makes the regular
  //    expressions stateful. Because of this, we need to
  //    recreate the regular expression *each time they are
  //    used*.

  // Step 1. Building the tokenized representation

  let tokenizedInput = input.replaceAll(
    // In the off chance the user entered something that looks
    // like our template variables, we should ignore it.
    parsing.templateVariable.toRegExp(),
    "",
  );

  /**
   * Before tokenizing the dates in the user input, we store those dates
   * in `dateMatches` for later use
   */
  let dateMatches: Array<RegExpMatchArray> | null = null;
  if (parsing.date.toRegExp().test(tokenizedInput)) {
    dateMatches = Array.from(tokenizedInput.matchAll(parsing.date.toRegExp()));

    tokenizedInput = tokenizedInput.replaceAll(parsing.date.toRegExp(), "{{DATE}}");
  }

  /**
   * Before tokenizing the times in the user input, we store those times
   * in `timeMatches` for later use
   */
  let timeMatches: Array<RegExpMatchArray> | null = null;
  if (parsing.timeWithColonOrSpaceAndMaybeMeridiem.toRegExp().test(tokenizedInput)) {
    timeMatches = Array.from(tokenizedInput.matchAll(parsing.timeWithColonOrSpaceAndMaybeMeridiem.toRegExp()));

    tokenizedInput = tokenizedInput.replaceAll(parsing.timeWithColonOrSpaceAndMaybeMeridiem.toRegExp(), "{{TIME}}");
  } else if (parsing.timeWithMeridiem.toRegExp().test(tokenizedInput)) {
    timeMatches = Array.from(tokenizedInput.matchAll(parsing.timeWithMeridiem.toRegExp()));

    tokenizedInput = tokenizedInput.replaceAll(parsing.timeWithMeridiem.toRegExp(), "{{TIME}}");
  }

  /**
   * Before tokenizing the numbers in the user input, we store those numbers
   * in `numberMatches` for later use
   */
  let numberMatches: Array<RegExpMatchArray> | null = null;
  if (parsing.number.toRegExp().test(tokenizedInput)) {
    numberMatches = Array.from(tokenizedInput.matchAll(parsing.number.toRegExp()));

    tokenizedInput = tokenizedInput.replaceAll(parsing.number.toRegExp(), "{{NUMBER}}");
  }

  // Step 2. Use our tokenized representation of the user input, we
  //         build a normalized version of the user input by
  //         reinserting our time, date, and number matches after
  //         having normalized them.

  let normalizedInput = tokenizedInput;
  const normalizedTimeMatches: string[] = [];
  const normalizedDateMatches: string[] = [];
  const normalizedNumberMatches: string[] = [];

  if (timeMatches) {
    for (const match of timeMatches) {
      const [, timeInput, meridiemInput] = match;
      const time = parsing.normalizeTimeFromInput(timeInput, meridiemInput);

      normalizedTimeMatches.push(time);
      normalizedInput = normalizedInput.replace("{{TIME}}", time);
    }
  }

  if (dateMatches) {
    for (const match of dateMatches) {
      const [, monthInput, dayInput, yearInput] = match;
      const date = parsing.normalizeDateFromInput(monthInput!, dayInput!, yearInput);

      normalizedDateMatches.push(date);
      normalizedInput = normalizedInput.replace("{{DATE}}", date);
    }
  }

  if (numberMatches) {
    for (const match of numberMatches) {
      const [, number] = match;

      normalizedNumberMatches.push(number!);
      normalizedInput = normalizedInput.replace("{{NUMBER}}", number!);
    }
  }

  // We also use our matched time, date, and number values to
  // update our suggestion templates to generate our list of suggestions.

  const matchedSuggestions = suggestions
    .map((suggestion) => {
      if (!suggestion) return null;

      if (timeMatches && suggestion.template.includes("{{TIME}}")) {
        for (const time of normalizedTimeMatches) {
          suggestion.update("{{TIME}}", time);
        }
      }

      if (dateMatches && suggestion.template.includes("{{DATE}}")) {
        for (const date of normalizedDateMatches) {
          suggestion.update("{{DATE}}", date);
        }
      }

      if (numberMatches && suggestion.template.includes("{{NUMBER}}")) {
        for (const number of normalizedNumberMatches) {
          suggestion.update("{{NUMBER}}", number);
        }
      }

      suggestion.finalizeUpdates(now);

      return suggestion;
    })
    // we filter out any suggestions that either don't have content
    // or which still have raw template variables that never received
    // a value. This would happen if, e.g., a suggestion was of the
    // form `{{NUMBER}} monday` but the user never provided a number.
    .filter(castToNonNullablePredicate((s) => !!s?.content && !s.disabled));

  let filteredSuggestions = matchedSuggestions.map((suggestion) => ({
    suggestion,
    date: suggestion.getDate(now),
    score: getHighestCommandScore(normalizedInput, suggestion.content!, ...suggestion.keywords),
  }));

  filteredSuggestions = filteredSuggestions
    .filter((result) => {
      if (result.score === 0) return false;

      switch (result.suggestion.valueType) {
        case "datetime":
        case "date":
        case "week":
        case "month":
        case "year": {
          if (allowDatesInThePast) {
            return !!result.date;
          } else {
            if (!result.date) return false;
            // We intentionally return invalid dates so that we can show an error message
            // to the user.
            if (!result.date.isValid) return true;
            return result.date > now;
          }
        }
        case null: {
          return true;
        }
        default: {
          throw new UnreachableCaseError(result.suggestion.valueType);
        }
      }
    })
    .sort((a, b) => b.score - a.score);

  if (filteredSuggestions.length > 0) {
    const highestScore = filteredSuggestions[0]!.score;

    // If we have a suggestion that seems like a good match, we shouldn't bother showing
    // other suggestions that have a very low score. Note that we are intentionally not
    // using a ratio based threshold (e.g. highestScore * 0.6) because a ratio based approach
    // allows for a wider range of allowed scores when the highest score is high, but we want
    // to tighten the range of allowed scores if the high score is high. Additionally,
    // if the highest score is close to zero, then we're essentially guessing and it makes sense
    // to just ignore this threshold.
    const threshold = highestScore - 0.2;

    filteredSuggestions = filteredSuggestions.filter((s) => s.score >= threshold);
  }

  filteredSuggestions = uniqBy(filteredSuggestions, (s) => s.suggestion.content);

  return filteredSuggestions.slice(0, limit).map((s) => s.suggestion);
}

function getHighestCommandScore(query: string, ...contents: string[]) {
  return Math.max(...contents.map((c) => commandScore(c, query)));
}
